/**
 * @module useEventListener
 * @description Custom hook for easily attaching event listeners to the document
 */

import { useEffect } from 'react';

/**
 * @function useEventListener
 * @description Attaches an event listener to the document that will be automatically removed on unmount
 *
 * @param {string} event - The event name to listen for (e.g., 'click', 'keydown', 'resize')
 * @param {Function} callback - The event handler function to be called when the event occurs
 *
 * @example
 * // Track window resize events
 * useEventListener('resize', () => {
 *   setWindowWidth(window.innerWidth);
 * });
 *
 * @example
 * // Handle keyboard shortcuts
 * useEventListener('keydown', (e) => {
 *   if (e.key === 'Escape') {
 *     closeModal();
 *   }
 * });
 */
export default function useEventListener(event, callback) {
  useEffect(() => {
    document.addEventListener(event, callback);
    return () => {
      document.removeEventListener(event, callback);
    };
  }, [event, callback]);
}
